import React, { createContext, useContext, useEffect, useState } from 'react'
import { areSectionConditionsMet, arrayWithOrderedIds, buildContentChanges, defaultAttachmentsForDocument } from '../../utils'
import { AttachmentsContext } from '../attachments/attachmentsState'
import byteConverter from '../../helpers/byteConverter'
import { StartValidationModal, ValidationSummaryModal, SingleDocumentCommentModal, ValidationSuggestionModal, SubmitCommentsModal, SingleDocumentSuggestionConfirmationModal, SharedDocumentPasswordModal, SharedDocumentErrorModal } from '../../components/misc'
import { ValidationContext } from '../validation/validationState'
import { NotificationContext } from '../notifications/notificationState'
import { UserContext } from '../user/userState'
import { DocumentsContext } from '../documents/documentsState'
import { add_document_event } from '../../services/firestore'
import { useSingleDocumentActions } from '../../hooks'

export const SingleDocumentContext = createContext()

const SingleDocumentState = ({ children, publicView }) => {
  const normalizeSectionIdxKey = (sectionIdx) => {
    if (sectionIdx === undefined || sectionIdx === null) {
      return ''
    }
    return String(sectionIdx)
  }

  const { documents, updateDocumentRevision, getSingleDocument } = useContext(DocumentsContext)
  const { defaultAttachments, defaultAttachmentsOrder } = useContext(AttachmentsContext)
  const { validations } = useContext(ValidationContext)
  const { setNotification } = useContext(NotificationContext)
  const { authError } = useContext(UserContext)
  const { downloadDocument } = useSingleDocumentActions()

  const [templateObjectWithUniqueVarIndexes, setTemplateObjectWithUniqueVarIndexes] = useState(null)
  const [documentId, setDocumentId] = useState(null)
  const [document, setDocument] = useState(null)
  const [documentValues, setDocumentValues] = useState({})
  const [documentAttachments, setDocumentAttachments] = useState([])
  const [partialContentChanges, setPartialContentChanges] = useState([])

  const [attachmentVariables, setAttachmentVariables] = useState([])
  const [filteredAttachmentVariables, setFilteredAttachmentVariables] = useState([])

  const [filteredDefaultAttachments, setFilteredDefaultAttachments] = useState([])

  const [filteredDocumentAttachments, setFilteredDocumentAttachments] = useState([]) // filter document attachments to exclude inactive attachment variables

  const [commentModal, setCommentModal] = useState({
    isOpen: false,
    section: null,
    variable: null,
  })
  const [newComments, setNewComments] = useState([])
  const [suggestionModal, setSuggestionModal] = useState({
    isOpen: false,
    suggestion: null,
    variable: null,
  })
  const [showSubmitCommentsModal, setShowSubmitCommentsModal] = useState(false)

  const [revisionModeEnabled, setRevisionModeEnabled] = useState(false)
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [showValidationModeSummary, setShowValidationModeSummary] = useState(false)
  const [activeValidation, setActiveValidation] = useState(null)

  const [suggestionConfirmationModal, setSuggestionConfirmationModal] = useState({
    isOpen: false,
    suggestion: null,
    validation: null,
    action: '',
  })

  const [sharedDocumentError, setSharedDocumentError] = useState({
    type: null, // 'expired' | 'invalid' | 'password-required
    title: null,
    message: null,
  })


  const addPublicComment = (comment) => {
    setNewComments((comments) => [...comments, comment])
  }

  const clearNewComments = () => {
    setNewComments([])
  }

  useEffect(() => {
    const doc = documents?.[documentId]
    setDocument(doc || null)
    if(doc?.password_required) {
      setSharedDocumentError({
        type: 'password-required',
        message: doc?.password_error || null,
      })
    } else if(doc?.share_expired) {
      setSharedDocumentError({
        type: 'expired',
      })
    } else if(authError) {
      setSharedDocumentError({
        type: 'invalid'
      })
    } else {
      setSharedDocumentError({
        type: null,
        message: null,
        title: null,
      })
    }
  }, [documentId, documents, authError])

  useEffect(() => {
    if(!documents[documentId]) {
      setShowValidationModeSummary(false)
      return
    }
    if(documents[documentId].password_required || documents[documentId].share_expired) {
      setShowValidationModeSummary(false)
      return
    }
  }, [documents, documentId])

  useEffect(() => {
    setShowValidationModeSummary(!publicView && revisionModeEnabled)
  }, [revisionModeEnabled, publicView])

  useEffect(() => {
    if(!documentId) {
      setActiveValidation(null)
      return
    }
    const sortedValidations = Object.values(validations).toSorted((a, b) => b.meta?.created - a.meta?.created)
    const pendingValidation = sortedValidations.find(v => v.documentId === documentId && v.status === 'pending')
    const refusedValidation = sortedValidations.find(v => v.documentId === documentId && v.status === 'refused')
    const approvedValidation = sortedValidations.find(v => v.documentId === documentId && v.status === 'validated')
    setActiveValidation(approvedValidation || pendingValidation || refusedValidation || null)
  }, [validations, documentId])

  useEffect(() => {
    if (!templateObjectWithUniqueVarIndexes) {
        return
    }
    const extractAttachmentVariables = (sections) => {
      const attachmentVars = []
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i]
        const isSectionAvailable = areSectionConditionsMet(section, documentValues)
        if (section.data_type === 'attachment') {
          attachmentVars.push({...section, available: isSectionAvailable})
        }
      }
      return attachmentVars
    }
    const attachmentVars = extractAttachmentVariables(templateObjectWithUniqueVarIndexes.sections)
    const filteredAttachmentVars = attachmentVars.filter(v => v.available)
    const uniqueAttachmentVars = attachmentVars.filter((v, i, a) => a.findIndex(t => (t.variable === v.variable)) === i)
    const uniqueFilteredAttachmentVars = filteredAttachmentVars.filter((v, i, a) => a.findIndex(t => (t.variable === v.variable)) === i)

    setAttachmentVariables([...uniqueAttachmentVars])
    setFilteredAttachmentVariables([...uniqueFilteredAttachmentVars])
  }, [templateObjectWithUniqueVarIndexes, documentValues])


  useEffect(() => {
    if (!defaultAttachments) {
      return 
    }
    let atts = Object.keys(defaultAttachments).map(key => ({
      ...defaultAttachments[key],
      id: key
    }))
    atts = defaultAttachmentsForDocument(defaultAttachments, templateObjectWithUniqueVarIndexes, documentValues)
    setFilteredDefaultAttachments(arrayWithOrderedIds(atts, defaultAttachmentsOrder).map(att => ({
      ...att,
      name_label: att.name,
      size_label: byteConverter(att.size)
    })))
  }, [defaultAttachments, defaultAttachmentsOrder, templateObjectWithUniqueVarIndexes, documentValues])

  useEffect(() => {
    const variableAttachments = {}
    for (let doc of documentAttachments) {
      if (doc.variable) {
        // attachmentValues[doc.id] = doc.value
        if (!variableAttachments[doc.variable]) {
          variableAttachments[doc.variable] = []
        }
        variableAttachments[doc.variable].push(doc)
      }
    }
    const valuesUpdates = {}
    for(let key in variableAttachments) {
      valuesUpdates[key] = variableAttachments[key].map(att => `${att.name}.${att.format}`).join(', ')
    }
    setDocumentValues((prev) => ({...prev, ...valuesUpdates}))
  }, [documentAttachments, setDocumentValues])

  useEffect(() => {
    setFilteredDocumentAttachments(documentAttachments.filter(att => {
      if(!att.variable) return true
      return filteredAttachmentVariables.find(v => v.variable === att.variable)
    }))
  }, [filteredAttachmentVariables, documentAttachments])

  const promptAddSuggestion = (suggestion) => {
    setSuggestionModal({
      isOpen: true,
      suggestion: suggestion,
    })
  }

  const closeSuggestionModal = () => {
    setSuggestionModal({
      isOpen: false,
      suggestion: null,
    })
  }

  const handleConfirmSuggestion = async () => {
    if(!suggestionModal.suggestion) {
      return
    }
    const { variable, value, type, repeatableIds, repetitionIndices, sectionIndex } = suggestionModal.suggestion
    const suggestionData = {
      variable: variable,
      value: value,
      type: type,
      repeatableIds: repeatableIds,
      repetitionIndices: repetitionIndices,
      sectionIndex: sectionIndex,
      date: Date.now(),
    }
    const res = await updateDocumentRevision(documentId, { suggestions: [suggestionData] })
    if(!res?.error) {
      setNotification({ msg: "Suggestion envoyée avec succès", type: 'success' })
      setSuggestionModal({
        isOpen: false,
        suggestion: null,
      })
    }
  }

  const promptAddComment = ({section, variable}) => {
    setCommentModal({
      isOpen: true,
      section: section,
      variable: variable
    })
  }

  const enableValidationMode = () => {
    setRevisionModeEnabled(true)
  }

  const disableValidationMode = () => {
    setRevisionModeEnabled(false)
  }

  const closeCommentModal = () => {
    setCommentModal({
      isOpen: false,
      section: null,
    })
  }

  const openValidationModal = () => {
    setShowValidationModal(true)
  }

  const closeValidationModal = () => {
    setShowValidationModal(false)
  }

  const openValidationModeSummary = () => {
    setShowValidationModeSummary(true)
  }

  const closeValidationModeSummary = () => {
    setShowValidationModeSummary(false)
  }

  const promptResolveSuggestion = async (suggestion, action) => {
    setSuggestionConfirmationModal({
      isOpen: true,
      suggestion,
      action,
    })
  }

  const promptSubmitComments = () => {
    if(!newComments || newComments.length === 0) {
      setNotification({ msg: "Aucun commentaire à envoyer", type: 'danger' })
      return
    }
    setShowSubmitCommentsModal(true)
    setCommentModal({
      isOpen: false,
      section: null,
      variable: null,
    })
  }

  const submitComments = async (generalComment) => {
    const commentsGeneral = []
    if(generalComment && generalComment.trim() !== '') {
      commentsGeneral.push({
        content: generalComment,
      }) 
    }
    const res = await updateDocumentRevision(documentId, { comments: newComments, commentsGeneral })
    if(!res?.error) {
      setNotification({ msg: "Commentaires envoyés avec succès", type: 'success' })
      setNewComments([])
      setShowSubmitCommentsModal(false)
    }
  }

  const handleCancelNewComments = () => {
    setNewComments([])
    setShowSubmitCommentsModal(false)
    setCommentModal({
      isOpen: false,
      section: null,
      variable: null,
    })
  }


  const handleSubmitSharedDocumentPassword = async (password) => {
    const docRes = await getSingleDocument(documentId, true, null, password)
    setDocumentValues(docRes?.values || {})
  }

  const addPartialContentChange = (change) => {
    if (!change) {
      return
    }
    setPartialContentChanges((existingChanges) => {
      const changes = [...(existingChanges || [])]
      let isNew = true
      const hasIndexedTarget = typeof change.itemIndex === 'number' && change.itemIndex >= 0
      const isRevertedToOriginal = (change.newContent ?? change.editedContent ?? '') === (change.originalContent ?? '')
      if (hasIndexedTarget) {
        for (let i = changes.length - 1; i >= 0; i--) {
          const sameSection = (
            changes[i].sectionIndex === change.sectionIndex &&
            changes[i].listIndex === change.listIndex &&
            normalizeSectionIdxKey(changes[i].sectionIdx) === normalizeSectionIdxKey(change.sectionIdx)
          )
          const hasNoIndexedTarget = !(typeof changes[i].itemIndex === 'number' && changes[i].itemIndex >= 0)
          const existingOriginal = changes[i].originalContent || ''
          const existingNew = changes[i].newContent ?? changes[i].editedContent ?? ''
          const incomingOriginal = change.originalContent || ''
          const incomingNew = change.newContent ?? change.editedContent ?? ''
          const overlaps =
            existingOriginal === incomingOriginal ||
            existingOriginal.includes(incomingOriginal) ||
            incomingOriginal.includes(existingOriginal) ||
            (existingNew && incomingNew && (existingNew === incomingNew || existingNew.includes(incomingNew) || incomingNew.includes(existingNew)))
          if (sameSection && hasNoIndexedTarget && overlaps) {
            changes.splice(i, 1)
          }
        }
      }
      for (let i = 0; i < changes.length; i++) {
        const sameSection = (
          changes[i].sectionIndex === change.sectionIndex &&
          changes[i].listIndex === change.listIndex &&
          normalizeSectionIdxKey(changes[i].sectionIdx) === normalizeSectionIdxKey(change.sectionIdx)
        )
        if (!sameSection) {
          continue
        }
        if (hasIndexedTarget) {
          if (
            changes[i].itemIndex === change.itemIndex &&
            changes[i].occurrenceIndex === change.occurrenceIndex
          ) {
            isNew = false
            if (isRevertedToOriginal) {
              changes.splice(i, 1)
            } else {
              changes[i] = {
                ...changes[i],
                ...change,
              }
            }
            break
          }
          continue
        }
        if (
          changes[i].originalContent === change.originalContent
        ) {
          isNew = false
          if (isRevertedToOriginal) {
            changes.splice(i, 1)
          } else {
            changes[i] = {
              ...changes[i],
              ...change,
            }
          }
          break
        }
      }
      if (isNew && !isRevertedToOriginal) {
        changes.push(change)
      }
      return changes
    })
  }

  const handleDocumentDownload = async ({
    extension = 'pdf',
    document: documentOverride,
    documentValues: documentValuesOverride,
    documentName,
    useCustomCover,
    linesAdded,
    renderOptions,
    templateObjectWithUniqueVarIndexes: templateOverride,
    isContentEditable = false,
    partialContentChanges: partialContentChangesOverride,
    templateObject
  } = {}) => {
    const targetDocument = documentOverride || document
    if (!targetDocument) {
      return
    }
    const resolvedPartialContentChanges = partialContentChangesOverride || partialContentChanges || []
    const values = documentValuesOverride || documentValues
    const templateWithUniqueVarIndexes = templateOverride || templateObjectWithUniqueVarIndexes

    setNotification({
      msg: 'Téléchargement du document...',
      type: 'info',
      position: 'top-right',
      duration: null
    })

    try {
      await downloadDocument(
        extension,
        {
          ...targetDocument,
          values: {
            ...(targetDocument.values || {}),
            ...(values || {})
          },
          name: documentName || targetDocument.name,
          custom_cover: useCustomCover,
          empty_lines_added: linesAdded,
        },
        renderOptions ? renderOptions(targetDocument, templateWithUniqueVarIndexes) : undefined
      )
      if (targetDocument.id) {
        await add_document_event(targetDocument.id, {
          type: `download_${extension}`,
          values: values || {},
          content_changes: isContentEditable ? buildContentChanges(resolvedPartialContentChanges, templateObject, []) : [],
        })
      }
    } finally {
      setNotification({ msg: '', type: 'default' })
    }
  }

  return <SingleDocumentContext.Provider value={{
    templateObjectWithUniqueVarIndexes,
    setTemplateObjectWithUniqueVarIndexes,
    setDocument,
    setDocumentId,
    document,
    documentId,
    documentValues,
    setDocumentValues,
    partialContentChanges,
    setPartialContentChanges,
    addPartialContentChange,
    filteredDefaultAttachments,
    attachmentVariables,
    filteredAttachmentVariables,
    documentAttachments,
    setDocumentAttachments,
    filteredDocumentAttachments,
    promptAddComment,
    revisionModeEnabled,
    enableValidationMode,
    disableValidationMode,
    openValidationModal,
    closeValidationModal,
    openValidationModeSummary,
    closeValidationModeSummary,
    activeValidation,
    promptAddSuggestion,
    publicView,
    promptResolveSuggestion,
    newComments,
    addPublicComment,
    promptSubmitComments,
    submitComments,
    clearNewComments,
    handleDocumentDownload
  }}>
    {children}

    {showValidationModal && (
        <StartValidationModal
            onClose={closeValidationModal}
            document={document}
            template={templateObjectWithUniqueVarIndexes}
            documentId={documentId}
        />
    )}
    {showValidationModeSummary && (
      <ValidationSummaryModal
        onClose={closeValidationModeSummary}
        documentId={documentId}
        validation={activeValidation}
        template={templateObjectWithUniqueVarIndexes}
        document={document}
      />
    )}
    {commentModal.isOpen && (
      <SingleDocumentCommentModal
        onClose={closeCommentModal}
        section={commentModal.section}
        variable={commentModal.variable}
        publicView={publicView}
      />
    )}
    {showSubmitCommentsModal && (
      <SubmitCommentsModal
        onClose={() => setShowSubmitCommentsModal(false)}
        onConfirm={(generalComment) => submitComments(generalComment)}
        onCancel={handleCancelNewComments}
        publicView={publicView}
      />
    )}
    {suggestionModal.isOpen && (
      <ValidationSuggestionModal
        onClose={closeSuggestionModal}
        suggestion={suggestionModal.suggestion}
        onConfirm={handleConfirmSuggestion}
        onCancel={closeSuggestionModal}
      />
    )}
    {suggestionConfirmationModal.isOpen && (
      <SingleDocumentSuggestionConfirmationModal
        onClose={() => setSuggestionConfirmationModal({ isOpen: false, suggestion: null, action: '' })}
        suggestion={suggestionConfirmationModal.suggestion}
        action={suggestionConfirmationModal.action}
      />
    )}
    {sharedDocumentError.type !== null && 
      (sharedDocumentError.type === 'password-required' ? (
        <SharedDocumentPasswordModal
          onClose={() => {}}
          onSubmit={handleSubmitSharedDocumentPassword}
          error={sharedDocumentError.message}
        />
      ) : (
        <SharedDocumentErrorModal type={sharedDocumentError.type} title={sharedDocumentError.title} message={sharedDocumentError.message} />
      )
    )}
  </SingleDocumentContext.Provider>
}


export default SingleDocumentState
